/**
 * VoidInvoiceDialog — the issued -> void confirm (invoice void affordance).
 *
 * Ruling (founder, void-not-delete): numbered financial documents never
 * vanish. Drafts delete (discardDraftInvoice); an ISSUED invoice voids
 * instead — the frozen invoice_snapshots row + doc_number stay on file
 * with a void mark (api/_shared/repos/invoices.ts voidInvoice, BILL-4 /
 * migration 0116). A reason is required server-side
 * (validateVoidInvoice: non-empty, <=500 chars) — the audit trail records
 * WHY, and it's the one field this dialog needs beyond confirmation, so it
 * owns its own submit (mirrors ArchiveRateDialog / HandoffDialog / SetRate
 * Dialog — the "dialog manages its own mutation" class — vs the plain
 * yes/no ConfirmDialog, which has no input to validate).
 *
 * States plainly where the charges go (founder copy, verbatim): voiding
 * frees the invoice's events (billed_on_invoice_id -> NULL server-side) so
 * they land back on the client's Pre-billing tab as Ready lines — an
 * operator should never wonder whether a void silently drops billable work.
 *
 * THE REFUSAL. The server reads the QuickBooks copy before voiding and
 * answers 409 qbo_copy_has_payment when a payment is applied to it. That
 * is rendered here as its own state with the facts the read returned
 * (which copy, when Olive looked, how much was paid), in the danger
 * register, telling the operator to unapply the payment in QuickBooks
 * first; the confirm button becomes the retry. It is not an error: the
 * invoice is untouched on purpose.
 *
 * Shared between InvoicePreview (the single-invoice action) and Invoices
 * (the list's per-row action) — one dialog, one copy, one error mapping.
 */

import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { ApiError, invoicesApi, type InvoiceWire } from '@/lib/api';
import { endSentence } from '@/lib/sentence';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

const REASON_MAX = 500;

/**
 * The server's refusal when the QuickBooks copy has a payment applied
 * (409 qbo_copy_has_payment): the facts it read, as they arrive on the
 * wire. Amounts are 2dp strings and null when the observation came from
 * storage, which holds the state but not the money.
 */
export interface VoidRefusedByPayment {
  qboInvoiceId: string;
  checkedAt: Date | null;
  source: 'live' | 'stored';
  totalAmt: string | null;
  balance: string | null;
  paymentApplied: string | null;
}

export function readVoidRefusal(err: unknown): VoidRefusedByPayment | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const body = err.body as Record<string, unknown>;
  if (body.error !== 'qbo_copy_has_payment') return null;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const checked = str(body.checked_at);
  const parsed = checked === null ? null : new Date(checked);
  return {
    qboInvoiceId: str(body.qbo_invoice_id) ?? '',
    checkedAt: parsed !== null && !Number.isNaN(parsed.getTime()) ? parsed : null,
    source: body.source === 'stored' ? 'stored' : 'live',
    totalAmt: str(body.total_amt),
    balance: str(body.balance),
    paymentApplied: str(body.payment_applied),
  };
}

/** "$1,234.50" from a 2dp string, or null when there is no amount. */
function money(v: string | null): string | null {
  if (v === null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function timeOfDay(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function voidErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: string }).error;
    if (code === 'illegal_transition') {
      return "Someone changed this invoice while you had it open, so it wasn't voided. Reload the page to see where it stands.";
    }
    if (code === 'invoice_not_found') {
      return 'This invoice is gone, so nothing was voided. Reload the page to see the current list.';
    }
    if (code === 'invalid_payload') {
      return 'Give a brief reason before voiding.';
    }
  }
  return contactSentence(
    "The invoice wasn't voided. Try again, and if it happens again, tell {contact_system}.",
    contact,
  );
}

export interface VoidInvoiceDialogProps {
  tenantSlug: string;
  invoiceId: string;
  docNumber: string | number | null;
  clientName: string;
  /** The sticky QuickBooks id when this invoice was pushed, null or
   *  absent otherwise. Olive's push is create-only (ruled #90,
   *  2026-08-25): voiding here NEVER voids the QuickBooks copy, and
   *  this dialog is the last screen before that becomes true, so when a
   *  copy exists the body says what does not happen to it. The list
   *  passes its row's qbo_invoice_id; the preview reads the shared
   *  push-state query. */
  qboInvoiceId?: string | null;
  onVoided: (invoice: InvoiceWire) => void;
  onClose: () => void;
}

export function VoidInvoiceDialog({
  tenantSlug,
  invoiceId,
  docNumber,
  clientName,
  qboInvoiceId = null,
  onVoided,
  onClose,
}: VoidInvoiceDialogProps): ReactNode {
  const contact = useSupportContact();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The refusal is its own state, not an error line: it is the one answer
  // where the invoice is untouched on purpose and the operator has a
  // specific thing to do somewhere else before trying again.
  const [refused, setRefused] = useState<VoidRefusedByPayment | null>(null);

  // The request is built where Void is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  const mut = useMutation({
    mutationFn: (voidReason: string) => invoicesApi.void(tenantSlug, invoiceId, voidReason),
    onSuccess: (res) => onVoided(res.invoice),
    onError: (err) => {
      const refusal = readVoidRefusal(err);
      if (refusal !== null) {
        setRefused(refusal);
        return;
      }
      setError(voidErrorMessage(err, contact));
    },
  });

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= REASON_MAX;

  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={docNumber !== null && docNumber !== '' ? `Void invoice #${docNumber}?` : 'Void this invoice?'}
      onClose={onClose}
      testId="void-invoice-dialog"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="void-invoice-cancel">
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setError(null);
              setRefused(null);
              mut.mutate(reason.trim());
            }}
            disabled={!canSubmit}
            loading={mut.isPending}
            data-testid="void-invoice-confirm"
          >
            {/* WHERE the void happens. "Void invoice" claimed the whole
                document; only the Olive half is Olive's to void. After a
                refusal the same button is the retry: the operator comes
                back once the payment is unapplied. */}
            {refused !== null ? 'Check again and void in Olive' : 'Void in Olive'}
          </Button>
        </>
      }
    >
      {/* One wrapper carries the size so the type-scale ratchet stays at
          this file's allowlisted single px utility. */}
      <div className="text-[13px] text-ink-soft mb-3 space-y-2">
        <p data-testid="void-invoice-body">
          Voiding keeps the numbered record with a void mark. Its charges return to{' '}
          {clientName}&rsquo;s Pre-billing tab as Ready lines, where they can be edited,
          re-billed, or removed.
        </p>
        {qboInvoiceId != null && (
          <p data-testid="void-invoice-qbo-note">
            This does not void the copy in QuickBooks (invoice {qboInvoiceId}). Until it is
            voided there, QuickBooks can still charge {endSentence(clientName)} Olive will check the copy
            right after and show what it finds on this invoice.
          </p>
        )}
      {refused !== null && (
        <div
          role="alert"
          data-testid="void-invoice-refused"
          className="rounded-tight border border-pepper px-3 py-2 text-pepper space-y-1"
        >
          <p className="font-semibold" data-testid="void-invoice-refused-title">
            Not voided, because the QuickBooks copy has a payment on it.
          </p>
          <p data-testid="void-invoice-refused-facts">
            {refused.paymentApplied !== null
              ? `When Olive read QuickBooks invoice ${refused.qboInvoiceId}${refused.checkedAt ? ` at ${timeOfDay(refused.checkedAt)}` : ''}, ${money(refused.paymentApplied)} of ${money(refused.totalAmt) ?? 'the total'} had been paid, leaving ${money(refused.balance) ?? 'a balance'} owing.`
              : `When Olive last read QuickBooks invoice ${refused.qboInvoiceId}${refused.checkedAt ? ` at ${timeOfDay(refused.checkedAt)}` : ''}, a payment was applied to it.`}
          </p>
          <p data-testid="void-invoice-refused-action">
            Unapply the payment in QuickBooks first, then come back and void it here. Voiding it now
            would leave the money as unapplied cash on {clientName}&rsquo;s customer record.
          </p>
        </div>
      )}
      </div>
      <label htmlFor="void-invoice-reason" className="block text-xs font-semibold text-ink mb-1">
        Reason
      </label>
      <textarea
        id="void-invoice-reason"
        data-testid="void-invoice-reason"
        className="w-full min-h-20 border border-sage-300 rounded-tight px-2 py-1 text-sm bg-paper text-ink"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={REASON_MAX}
        placeholder="Why is this invoice being voided?"
      />
      {error && (
        <p role="alert" data-testid="void-invoice-error" className="text-xs text-pepper mt-2">
          {error}
        </p>
      )}
    </Dialog>
  );
}
